import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { scrollToAlert, stringify } from '@lib/utils';
import { PageContext } from '@lib/contexts/pageContext';

function useErrorHandling({
  errors: formErrors,
  prevErrors,
  isSubmitting,
  wasSubmitting,
  setWasSubmitting,
  scrolling = true,
  modalContext = null,
}) {
  const [wasErrorShown, setWasErrorShown] = useState(false);

  const errors = useMemo(() => formErrors || {}, [formErrors]);

  let { setPageMessage, setPageErrorMessage } = useContext(PageContext);
  if (modalContext) {
    // If the form is contained in a modal, then we need to use different message handlers, hence the override
    ({ setPageMessage, setPageErrorMessage } = modalContext);
  }

  const handleErrors = useCallback(() => {
    // Calculate the error state first (needed for early clearing check)
    const errorValues = Object.values(errors);

    const [missingRequired, otherErrors] = errorValues.reduce((acc, error) => {
      let flattenedErrors = [];
      if (error.type) {
        flattenedErrors = [error];
      } else {
        // nested error message, from some forms like Event Demographics with grouped fields
        flattenedErrors = Object.values(error);
      }
      flattenedErrors.forEach((e) => {
        if (e.type === 'required') {
          acc[0] = true;
        } else {
          if (!acc[1].includes(e.message)) {
            acc[1].push(e.message);
          }
        }
      });
      return acc;
    }, [false, []]);

    const hasErrors = missingRequired || !!otherErrors.length;

    // Clear error messages when all errors are resolved - check BEFORE early return
    // This handles React state batching where formErrors and prevErrors may both be empty
    if (!hasErrors && wasErrorShown) {
      setWasErrorShown(false);
      setPageErrorMessage(null);
      return;
    }

    if (stringify(formErrors, false) === stringify(prevErrors, false) && isSubmitting === wasSubmitting) {
      return;
    }

    // Here tracks if the form was submitted on the last render (meaning submit was clicked and validation is occurring)
    if (isSubmitting) {
      setWasSubmitting(true);
    } else if (wasSubmitting) {
      setWasSubmitting(false);
    }

    if (hasErrors) {
      // Only show the alert if the form was actually submitted via the button
      if (wasSubmitting) {
        const messages = [];
        if (missingRequired) {
          messages.push('Please fill out all required (*) fields.');
        }
        if (otherErrors.length) {
          messages.push(...otherErrors)
        }
        if (messages.length) {
          setPageMessage(null);
          setPageErrorMessage(messages);
          setWasErrorShown(true);
        }
        if (scrolling) {
          scrollToAlert();
        }
      }
    }
  }, [
    errors,
    formErrors,
    prevErrors,
    isSubmitting,
    scrolling,
    setPageErrorMessage,
    setPageMessage,
    setWasSubmitting,
    wasErrorShown,
    wasSubmitting
  ]);

  useEffect(() => {
    handleErrors();
  }, [handleErrors]);
}

export default useErrorHandling;
